/** Subscription module access helpers for nav and route guards. */

import { isSubscriptionActive, isSubscriptionModuleEnabled, getSubscription } from './subscriptionStatus';

export const MODULE_DESCRIPTIONS = {
  students: 'Manage student records, admissions, profiles, and documents.',
  staff: 'Manage teachers and staff profiles, roles, and HR records.',
  attendance: 'Track student and staff attendance with daily reports.',
  fees: 'Collect fees, manage fee structures, accounts, and receipts.',
  payroll: 'Calculate and process teacher salaries with attendance-based payroll.',
  exams: 'Create exams, schedules, results, and publish report cards.',
  library: 'Manage library books, issues, returns, and catalog.',
  transport: 'Manage routes, vehicles, drivers, and student transport assignments.',
  hostel: 'Manage hostel rooms, allocations, and resident records.',
  reception: 'Handle visitors, inquiries, appointments, and front-desk operations.',
  reports: 'Generate report cards, certificates, ID cards, and attendance reports.',
  sms: 'Send SMS notifications to parents and staff.',
  whatsapp: 'WhatsApp broadcasts, announcements, and message logs.',
  mobile_app: 'Mobile app access for parents, students, and staff.',
};

export const MODULE_LABELS = {
  students: 'Students',
  staff: 'Teachers',
  attendance: 'Attendance',
  fees: 'Fees',
  payroll: 'Payroll',
  exams: 'Exams',
  library: 'Library',
  transport: 'Transport',
  hostel: 'Hostel',
  reception: 'Reception',
  reports: 'Reports',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  mobile_app: 'Mobile App',
};

/** Nav path prefix → subscription module key. Longest match wins. */
const NAV_PATH_MODULE_RULES = [
  { prefix: '/whatsapp', module: 'whatsapp' },
  { prefix: '/reports/whatsapp-logs', module: 'whatsapp' },
  { prefix: '/reports', module: 'reports' },
  { prefix: '/reception', module: 'reception' },
  { prefix: '/library', module: 'library' },
  { prefix: '/transport', module: 'transport' },
  { prefix: '/exams', module: 'exams' },
  { prefix: '/fees', module: 'fees' },
  { prefix: '/attendance', module: 'attendance' },
  { prefix: '/leave', module: 'attendance' },
  { prefix: '/students', module: 'students' },
  { prefix: '/hr/teachers', module: 'staff' },
  { prefix: '/teachers/attendance', module: 'staff' },
];

const CORE_PATHS = new Set([
  '/dashboard',
  '/profile',
  '/subjects',
  '/classes',
  '/timetable',
  '/events',
  '/assignments',
  '/announcements',
]);

export function subscriptionModuleForPath(path) {
  if (!path) return null;
  if (CORE_PATHS.has(path)) return null;

  const normalized = path.split('?')[0];
  for (const rule of NAV_PATH_MODULE_RULES) {
    if (normalized === rule.prefix || normalized.startsWith(`${rule.prefix}/`)) {
      return rule.module;
    }
  }
  return null;
}

export function getEnabledModules(user) {
  const sub = getSubscription(user);
  if (Array.isArray(sub.enabled_modules) && sub.enabled_modules.length > 0) {
    return sub.enabled_modules;
  }
  const modules = sub.features?.modules ?? {};
  return Object.entries(modules)
    .filter(([, enabled]) => Boolean(enabled))
    .map(([key]) => ({ key, label: MODULE_LABELS[key] || key }));
}

export function getLockedModules(user) {
  const sub = getSubscription(user);
  if (Array.isArray(sub.locked_modules) && sub.locked_modules.length > 0) {
    return sub.locked_modules;
  }
  const modules = sub.features?.modules ?? {};
  return Object.entries(modules)
    .filter(([, enabled]) => !enabled)
    .map(([key]) => ({ key, label: MODULE_LABELS[key] || key }));
}

export function canUseModule(user, moduleKey) {
  if (!moduleKey) return true;
  if (user?.role_slug === 'super_admin') return true;
  if (!isSubscriptionActive(user)) return false;
  return isSubscriptionModuleEnabled(user, moduleKey);
}

export function isModuleLocked(user, pathOrModule) {
  if (!user || user.role_slug === 'super_admin') return false;

  const moduleKey = pathOrModule.startsWith?.('/')
    ? subscriptionModuleForPath(pathOrModule)
    : pathOrModule;

  if (!moduleKey) return false;
  if (!isSubscriptionActive(user)) return true;

  return !isSubscriptionModuleEnabled(user, moduleKey);
}

export function isNavPathLocked(user, path) {
  return isModuleLocked(user, path);
}

export function canNavigateToPath(user, path) {
  if (!path) return false;
  if (path === '/dashboard' || path === '/profile') return true;
  if (isNavPathLocked(user, path)) return false;
  return true;
}

export function moduleDescription(moduleKey) {
  return MODULE_DESCRIPTIONS[moduleKey] || 'Premium module for your school.';
}

export function moduleLabel(moduleKey) {
  return MODULE_LABELS[moduleKey] || moduleKey;
}
